import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getBindings, createMpOauthLink, getAppids, addAppid, deleteAppid, updateAppid } from '@/services/api'
import { useToastStore } from '@/stores/toast'
import QRCode from 'qrcode'
import { useAuth } from '@/contexts/AuthContext'
import Header from '@/components/Header'

interface Binding {
  id: string
  user_id: string
  phone: string
  appid: string
  openid: string
  unionid?: string
  channel: 'mp' | 'mini' | 'h5'
  created_at: string
  updated_at: string
}

export default function Bindings() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [filters, setFilters] = useState<{ phone: string; appid: string; channel: string; page: number; limit: number; days?: number; order?: 'asc' | 'desc' }>({ phone: '', appid: '', channel: '', page: 1, limit: 10, days: undefined, order: 'desc' })
  const [list, setList] = useState<Binding[]>([])
  const [total, setTotal] = useState(0)
  const [gen, setGen] = useState<{ phone: string; appid: string; scope: 'snsapi_base' | 'snsapi_userinfo'; callbackBase: string; fillByUser: boolean }>({ phone: '', appid: '', scope: 'snsapi_base', callbackBase: '', fillByUser: false })
  const [authUrl, setAuthUrl] = useState('')
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [appids, setAppids] = useState<{ id: string; appid: string; name?: string; channel: 'mp' | 'mini' | 'app' | 'h5' }[]>([])
  const [showAppidMgr, setShowAppidMgr] = useState(false)
  const [newAppid, setNewAppid] = useState<{ appid: string; name: string; channel: 'mp' | 'mini' | 'app' | 'h5'; secret?: string }>({ appid: '', name: '', channel: 'mp', secret: '' })
  const [editId, setEditId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<{ appid: string; name: string; channel: 'mp' | 'mini' | 'app' | 'h5'; secret?: string }>({ appid: '', name: '', channel: 'mp', secret: '' })
  const { show } = useToastStore()
  const [qrOpen, setQrOpen] = useState(false)
  const [qrDataUrl, setQrDataUrl] = useState<string>('')
  const [shareOpen, setShareOpen] = useState(false)
  const [rowShareOpen, setRowShareOpen] = useState<string | null>(null)
  const [appidOpen, setAppidOpen] = useState(false)
  const [appidQuery, setAppidQuery] = useState('')

  useEffect(() => { loadBindings() }, [filters])
  useEffect(() => { fetchAppids() }, [user?.activeMerchantId])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShareOpen(false)
        setRowShareOpen(null)
        setQrOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const roots = document.querySelectorAll('[data-share-root="true"],[data-combo-root="true"]')
      let inside = false
      roots.forEach((el) => { if ((el as HTMLElement).contains(target)) inside = true })
      if (!inside) {
        setShareOpen(false)
        setRowShareOpen(null)
        setAppidOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [])

  const loadBindings = async () => {
    try {
      setLoading(true)
      const res = await getBindings(filters)
      setList(res.data?.bindings ?? [])
      setTotal(res.data?.pagination?.total ?? 0)
    } catch (e) {
      setToast(null as any)
      show({ type: 'error', message: '加载绑定列表失败' })
    } finally {
      setLoading(false)
    }
  }

  const fetchAppids = async () => {
    try {
      const r = await getAppids()
      setAppids(r.data?.appids ?? [])
    } catch {}
  }

  const generate = async () => {
    if (!gen.appid) { show({ type: 'error', message: '请选择AppID' }); return }
    try {
      const res = await createMpOauthLink({ phone: gen.fillByUser ? undefined : gen.phone, appid: gen.appid, scope: gen.scope, callbackBase: gen.callbackBase || undefined })
      const url = res.data?.url || ''
      setAuthUrl(url)
      try { await navigator.clipboard.writeText(url) } catch {}
      show({ type: 'success', message: '授权链接已生成并复制到剪贴板' })
    } catch (e) {
      show({ type: 'error', message: '生成授权链接失败' })
    }
  }

  const goTransfer = (b: Binding) => {
    navigate(`/transfer/create?phone=${encodeURIComponent(b.phone)}&appid=${encodeURIComponent(b.appid)}&openid=${encodeURIComponent(b.openid)}`)
  }

  const pageCount = Math.ceil(total / filters.limit)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="绑定管理" backTo="/dashboard" right={<button onClick={()=>setShowAppidMgr(true)} className="bg-wechat-green hover:bg-wechat-dark text-white px-4 py-2 rounded-md text-sm font-medium">管理AppID</button>} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-wechat-green text-white flex items-center justify-center">1</span>
            <span>选择应用</span>
          </div>
          <div className="flex-1 h-px bg-gray-200"></div>
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-wechat-green text-white flex items-center justify-center">2</span>
            <span>填写手机号或让用户填写</span>
          </div>
          <div className="flex-1 h-px bg-gray-200"></div>
          <div className="flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-wechat-green text-white flex items-center justify-center">3</span>
            <span>生成并分享授权链接</span>
          </div>
        </div>

        {toast && (
          <div className={`mb-4 rounded border px-4 py-2 ${toast.type==='success' ? 'border-green-300 bg-green-50 text-green-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{toast.msg}</div>
        )}

        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold mb-3">生成授权链接（公众号）</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <input className="border rounded px-3 py-2" placeholder="手机号（可留空让用户填写）" value={gen.phone} onChange={e=>setGen({ ...gen, phone: e.target.value })} disabled={gen.fillByUser} />
            <div className="relative" data-combo-root="true">
              <button type="button" className="border rounded px-3 py-2 w-full text-left flex items-center justify-between" onClick={()=>setAppidOpen(v=>!v)}>
                <span>
                  {(() => { const sel = appids.find(a=>a.appid===gen.appid && a.channel==='mp'); return sel ? (sel.name || sel.appid) + `（${sel.appid}｜${sel.channel}）` : '选择AppID'} )()}
                </span>
                <span>▾</span>
              </button>
              {appidOpen && (
                <div className="absolute left-0 top-full mt-2 w-[28rem] max-w-[calc(100vw-2rem)] bg-white border rounded shadow z-50">
                  <div className="p-2 border-b">
                    <input className="border rounded px-2 py-1 w-full" placeholder="搜索名称或AppID" value={appidQuery} onChange={e=>setAppidQuery(e.target.value)} />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {appids.filter(a=>a.channel==='mp').filter(a=>{ const q=appidQuery.trim().toLowerCase(); if(!q) return true; return (a.name||'').toLowerCase().includes(q) || a.appid.toLowerCase().includes(q) }).map(a => (
                      <button key={a.id} className="block w-full text-left px-3 py-2 hover:bg-gray-50" onClick={()=>{ setGen({ ...gen, appid: a.appid }); setAppidOpen(false) }}>
                        <div className="font-medium">{a.name || a.appid}</div>
                        <div className="text-xs text-gray-500">{a.appid}｜{a.channel}</div>
                      </button>
                    ))}
                    {appids.filter(a=>a.channel==='mp').length===0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">暂无公众号 AppID</div>
                    )}
                  </div>
                  <div className="p-2 border-t flex items-center justify-between">
                    <button className="px-3 py-1 border rounded" onClick={()=>{ setGen({ ...gen, appid: '' }); setAppidOpen(false) }}>清除</button>
                    <button className="px-3 py-1 border rounded" onClick={()=>setShowAppidMgr(true)}>管理AppID</button>
                  </div>
                </div>
              )}
            </div>
            <select className="border rounded px-3 py-2" value={gen.scope} onChange={e=>setGen({ ...gen, scope: e.target.value as any })}>
              <option value="snsapi_base">snsapi_base（静默，仅openid）</option>
              <option value="snsapi_userinfo">snsapi_userinfo（需同意，含资料）</option>
            </select>
            <input className="border rounded px-3 py-2" placeholder="回调API基域（可选）" value={gen.callbackBase} onChange={e=>setGen({ ...gen, callbackBase: e.target.value })} />
          </div>
          <div className="mt-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={gen.fillByUser} onChange={e=>setGen({ ...gen, fillByUser: e.target.checked })} />
              让授权方在绑定完成页填写手机号/用户名
            </label>
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={async()=>{ await generate(); }} className="bg-wechat-green hover:bg-wechat-dark text-white px-4 py-2 rounded">生成并复制链接</button>
            {authUrl && <a href={authUrl} target="_blank" rel="noreferrer" className="text-wechat-green">预览链接 →</a>}
            {authUrl && (
              <div className="relative inline-block" data-share-root="true">
                <button className="inline-flex items-center gap-2 px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-wechat-green" onClick={()=>setShareOpen(v=>!v)} aria-haspopup="menu" aria-expanded={shareOpen}>
                  <span>📤</span>
                  <span>分享</span>
                  <span className="ml-1">▾</span>
                </button>
                {shareOpen && (
                  <div className="absolute left-0 top-full mt-2 w-56 bg-white border rounded shadow z-30" onClick={(e)=>e.stopPropagation()} role="menu">
                    <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { await navigator.clipboard.writeText(authUrl); show({ type: 'success', message: '链接已复制' }) } catch { show({ type: 'error', message: '复制失败，请手动复制' }) } }} role="menuitem"><span>📋</span><span>复制链接</span></button>
                    <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { if ((navigator as any).share) { await (navigator as any).share({ title: '微信授权绑定', url: authUrl }) ; show({ type: 'success', message: '已通过系统分享' }) } else { show({ type: 'error', message: '设备不支持系统分享' }) } } catch {} }} role="menuitem"><span>📤</span><span>系统分享链接</span></button>
                    <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { const d = await QRCode.toDataURL(authUrl, { width: 320, margin: 1 }); setQrOpen(true); setQrDataUrl(d); setShareOpen(false) } catch { show({ type: 'error', message: '二维码生成失败' }) } }} role="menuitem"><span>🔳</span><span>二维码预览</span></button>
                  </div>
                )}
              </div>
            )}
          </div>
          {gen.appid && (
            <div className="mt-2 text-sm text-gray-600">
              {(() => {
                const sel = appids.find(a=>a.appid===gen.appid)
                if (!sel) return null
                return <span>已选择应用：<b>{sel.name || sel.appid}</b>（AppID：{sel.appid}，渠道：{sel.channel}）{sel.channel!=='mp' && ' —— 仅公众号支持生成授权链接，其它渠道请在客户端获取openid后调用绑定接口'}</span>
              })()}
            </div>
          )}
        </div>

        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex flex-wrap gap-3 mb-3">
            <input className="border rounded px-3 py-2" placeholder="搜索手机号" value={filters.phone} onChange={e=>setFilters({ ...filters, phone: e.target.value, page: 1 })} />
            <input className="border rounded px-3 py-2" placeholder="AppID" value={filters.appid} onChange={e=>setFilters({ ...filters, appid: e.target.value, page: 1 })} />
            <select className="border rounded px-3 py-2" value={filters.channel} onChange={e=>setFilters({ ...filters, channel: e.target.value, page: 1 })}>
              <option value="">全部渠道</option>
              <option value="mp">公众号</option>
              <option value="mini">小程序</option>
              <option value="app">APP</option>
              <option value="h5">H5</option>
            </select>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">时间：</span>
              <button className={`px-3 py-1 border rounded ${!filters.days ? 'bg-gray-100' : ''}`} onClick={()=>setFilters({ ...filters, days: undefined, page: 1 })}>全部</button>
              <button className={`px-3 py-1 border rounded ${filters.days===7 ? 'bg-gray-100' : ''}`} onClick={()=>setFilters({ ...filters, days: 7, page: 1 })}>近7天</button>
              <button className={`px-3 py-1 border rounded ${filters.days===30 ? 'bg-gray-100' : ''}`} onClick={()=>setFilters({ ...filters, days: 30, page: 1 })}>近30天</button>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">排序：</span>
              <select className="border rounded px-2 py-1" value={filters.order} onChange={e=>setFilters({ ...filters, order: e.target.value as any })}>
                <option value="desc">按更新时间（降序）</option>
                <option value="asc">按更新时间（升序）</option>
              </select>
            </div>
            <button className="px-3 py-1 border rounded" onClick={()=>setFilters({ phone:'', appid:'', channel:'', page:1, limit:10, days: undefined, order:'desc' })}>清空筛选</button>
          </div>

          {loading ? (
            <div className="space-y-2 animate-pulse">
              <div className="h-4 bg-gray-100 rounded"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6"></div>
              <div className="h-4 bg-gray-100 rounded w-4/6"></div>
            </div>
          ) : list.length === 0 ? (
            <div className="text-center text-gray-500 py-8">暂无授权用户，先在上方生成并分享授权链接</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left">手机号</th>
                    <th className="px-3 py-2 text-left">应用名称</th>
                    <th className="px-3 py-2 text-left">AppID</th>
                    <th className="px-3 py-2 text-left">渠道</th>
                    <th className="px-3 py-2 text-left">OpenID（脱敏）</th>
                    <th className="px-3 py-2 text-left">更新时间</th>
                    <th className="px-3 py-2 text-left">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {list.map((b) => (
                    <tr key={b.id}>
                      <td className="px-3 py-2">{b.phone}</td>
                      <td className="px-3 py-2">{(() => { const x = appids.find(a=>a.appid===b.appid); return x ? (x.name || '-') : '-' })()}</td>
                      <td className="px-3 py-2">{b.appid}</td>
                      <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{b.channel.toUpperCase()}</span></td>
                      <td className="px-3 py-2">{b.openid.slice(0,6)}****{b.openid.slice(-4)}</td>
                      <td className="px-3 py-2">{(() => { const s=String(b.updated_at||''); const d = s.includes('T') ? new Date(s) : new Date(s.replace(' ', 'T')); return isNaN(d.getTime()) ? s : d.toLocaleString() })()}</td>
                      <td className="px-3 py-2 flex gap-2 items-center">
                        <div className="relative inline-block" data-share-root="true">
                          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-md text-wechat-green focus:outline-none focus:ring-2 focus:ring-wechat-green" onClick={()=>setRowShareOpen(b.id)} aria-haspopup="menu" aria-expanded={rowShareOpen===b.id}>
                            <span>📤</span>
                            <span>分享</span>
                            <span className="ml-0.5">▾</span>
                          </button>
                          {rowShareOpen===b.id && (
                            <div className="absolute left-0 top-full mt-2 w-56 bg-white border rounded shadow z-30" onClick={(e)=>e.stopPropagation()} role="menu">
                              <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { const res = await createMpOauthLink({ phone: b.phone, appid: b.appid }); const url = res.data?.url || ''; await navigator.clipboard.writeText(url); show({ type: 'success', message: '链接已复制' }) } catch { show({ type: 'error', message: '复制失败，请手动复制' }) } }} role="menuitem"><span>📋</span><span>复制链接</span></button>
                              <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { const res = await createMpOauthLink({ phone: b.phone, appid: b.appid }); const url = res.data?.url || ''; if ((navigator as any).share) { await (navigator as any).share({ title: '微信授权绑定', url }); show({ type: 'success', message: '已通过系统分享' }) } else { show({ type: 'error', message: '设备不支持系统分享' }) } } catch {} }} role="menuitem"><span>📤</span><span>系统分享链接</span></button>
                              <button className="block w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-2" onClick={async()=>{ try { const res = await createMpOauthLink({ phone: b.phone, appid: b.appid }); const url = res.data?.url || ''; const d = await QRCode.toDataURL(url, { width: 320, margin: 1 }); setQrOpen(true); setQrDataUrl(d); setRowShareOpen(null) } catch { show({ type: 'error', message: '二维码生成失败' }) } }} role="menuitem"><span>🔳</span><span>二维码预览</span></button>
                            </div>
                          )}
                        </div>
                        <button className="text-blue-600" onClick={()=>goTransfer(b)}>去转账</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center gap-2">
                <button disabled={filters.page<=1} onClick={()=>setFilters({ ...filters, page: filters.page-1 })} className="px-3 py-1 border rounded">上一页</button>
                <span>第 {filters.page} / {pageCount || 1} 页</span>
                <button disabled={filters.page>=pageCount} onClick={()=>setFilters({ ...filters, page: filters.page+1 })} className="px-3 py-1 border rounded">下一页</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showAppidMgr && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">管理AppID</h3>
              <button onClick={()=>setShowAppidMgr(false)} className="text-gray-600">✕</button>
            </div>
            <div className="mb-3 grid grid-cols-1 md:grid-cols-4 gap-2">
              <input className="border rounded px-3 py-2" placeholder="AppID" value={newAppid.appid} onChange={e=>setNewAppid({ ...newAppid, appid: e.target.value })} />
              <input className="border rounded px-3 py-2" placeholder="名称（可选）" value={newAppid.name} onChange={e=>setNewAppid({ ...newAppid, name: e.target.value })} />
              <select className="border rounded px-3 py-2" value={newAppid.channel} onChange={e=>setNewAppid({ ...newAppid, channel: e.target.value as any })}>
                <option value="mp">公众号</option>
                <option value="mini">小程序</option>
                <option value="app">APP</option>
                <option value="h5">H5</option>
              </select>
              <div></div>
            </div>
            <div className="mb-2">
              {(newAppid.channel==='mp' || newAppid.channel==='mini') && (
                <input className="border rounded px-3 py-2 w-full" placeholder={newAppid.channel==='mp'?'公众号密钥（AppSecret）':'小程序密钥（AppSecret）'} value={newAppid.secret||''} onChange={e=>setNewAppid({ ...newAppid, secret: e.target.value })} />
              )}
            </div>
            <div className="mb-4">
              <button className="bg-wechat-green text-white px-4 py-2 rounded" onClick={async()=>{ try { await addAppid(newAppid); await fetchAppids(); setNewAppid({ appid: '', name: '', channel: 'mp', secret: '' }); show({ type:'success', message:'AppID已保存' }) } catch { show({ type:'error', message:'保存失败' }) } }}>保存</button>
            </div>
            <div className="overflow-x-auto max-h-[60vh] overflow-y-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left">名称</th>
                    <th className="px-3 py-2 text-left">AppID</th>
                    <th className="px-3 py-2 text-left">渠道</th>
                    <th className="px-3 py-2 text-left">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {appids.map(a => (
                    <tr key={a.id}>
                      {editId===a.id ? (
                        <>
                          <td className="px-3 py-2">
                            <input className="border rounded px-2 py-1 w-full" value={editForm.name} placeholder="名称" onChange={e=>setEditForm({ ...editForm, name: e.target.value })} />
                          </td>
                          <td className="px-3 py-2">
                            <input className="border rounded px-2 py-1 w-full" value={editForm.appid} placeholder="AppID" onChange={e=>setEditForm({ ...editForm, appid: e.target.value })} />
                          </td>
                          <td className="px-3 py-2">
                            <select className="border rounded px-2 py-1 w-full" value={editForm.channel} onChange={e=>setEditForm({ ...editForm, channel: e.target.value as any })}>
                              <option value="mp">公众号</option>
                              <option value="mini">小程序</option>
                              <option value="app">APP</option>
                              <option value="h5">H5</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            {(editForm.channel==='mp' || editForm.channel==='mini') ? (
                              <input className="border rounded px-2 py-1 w-full" value={editForm.secret||''} placeholder={editForm.channel==='mp'?'公众号密钥（AppSecret）':'小程序密钥（AppSecret）'} onChange={e=>setEditForm({ ...editForm, secret: e.target.value })} />
                            ) : (
                              <span className="text-xs text-gray-500">该渠道无需密钥</span>
                            )}
                          </td>
                          <td className="px-3 py-2 flex gap-2">
                            <button className="bg-wechat-green text-white px-3 py-1 rounded" onClick={async()=>{ try { await updateAppid(String(a.id), editForm); await fetchAppids(); setEditId(null); show({ type: 'success', message: '已更新AppID' }) } catch { show({ type: 'error', message: '更新失败' }) } }}>保存</button>
                            <button className="px-3 py-1 border rounded" onClick={()=>{ setEditId(null) }}>取消</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2">{a.name || '-'}</td>
                          <td className="px-3 py-2">{a.appid}</td>
                          <td className="px-3 py-2">{a.channel}</td>
                          <td className="px-3 py-2 flex gap-3">
                            <button className="text-wechat-green" onClick={()=>{ setEditId(a.id); setEditForm({ appid: a.appid, name: a.name || '', channel: a.channel }) }}>编辑</button>
                            <button className="text-red-600" onClick={async()=>{ try { await deleteAppid(String(a.id)); await fetchAppids(); show({ type: 'success', message: '已删除AppID' }) } catch { show({ type: 'error', message: '删除失败' }) } }}>删除</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {qrOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-40">
          <div className="bg-white rounded-lg p-5 w-full max-w-sm text-center">
            <div className="text-lg font-semibold mb-3">扫码授权</div>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="授权二维码" className="mx-auto border rounded" />
            ) : (
              <div className="h-64 w-64 bg-gray-100 animate-pulse mx-auto rounded" />
            )}
            <div className="mt-3 flex justify-center gap-3">
              <a href={qrDataUrl} download="wechat-bind-qr.png" className="px-3 py-2 border rounded">下载二维码</a>
              <button className="px-3 py-2 border rounded" onClick={async()=>{
                try {
                  const resp = await fetch(qrDataUrl)
                  const blob = await resp.blob()
                  const file = new File([blob], 'wechat-bind-qr.png', { type: 'image/png' })
                  if ((navigator as any).canShare && (navigator as any).canShare({ files: [file] })) {
                    await (navigator as any).share({ files: [file], title: '微信授权绑定', text: '扫码授权绑定' })
                    show({ type: 'success', message: '已通过系统分享二维码' })
                  } else {
                    const url = URL.createObjectURL(blob)
                    window.open(url, '_blank')
                  }
                } catch {
                  show({ type: 'error', message: '分享失败，请手动保存' })
                }
              }}>分享二维码</button>
              <button className="px-3 py-2 border rounded" onClick={()=>setQrOpen(false)}>关闭</button>
            </div>
          </div>
        </div>
      )}

      {authUrl && (
        <div className="fixed bottom-3 left-0 right-0 px-4 md:hidden">
          <div className="bg-white border rounded-xl shadow flex items-center justify-between px-3 py-2">
            <button className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-wechat-green flex items-center gap-2" onClick={async()=>{ try { await navigator.clipboard.writeText(authUrl); show({ type: 'success', message: '链接已复制' }) } catch { show({ type: 'error', message: '复制失败，请手动复制' }) } }}><span>📋</span><span>复制</span></button>
            <button className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-wechat-green flex items-center gap-2" onClick={async()=>{ try { if ((navigator as any).share) { await (navigator as any).share({ title: '微信授权绑定', url: authUrl }); show({ type: 'success', message: '已通过系统分享' }) } else { show({ type: 'error', message: '设备不支持系统分享' }) } } catch {} }}><span>📤</span><span>分享</span></button>
            <button className="px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-wechat-green flex items-center gap-2" onClick={async()=>{ try { const d = await QRCode.toDataURL(authUrl, { width: 320, margin: 1 }); setQrOpen(true); setQrDataUrl(d) } catch { show({ type: 'error', message: '二维码生成失败' }) } }}><span>🔳</span><span>二维码</span></button>
          </div>
        </div>
      )}
    </div>
  )
}
